import React from "react";
import { FileText, Heart, List, ShieldCheck } from "lucide-react";
import { useLocation } from "react-router-dom";
import { ROUTES } from "../../../constants/common";
import { HHTabs, TabsMap } from "../Tabs";

type MarketNavigationProps = {
  canModerateListings?: boolean;
  className?: string;
};

const normalizePathname = (pathname: string): string =>
  pathname.replace(/\/+$/, "") || "/";

const MY_LISTINGS_TABS = new Set(["published", "moderation", "unpublished"]);

type MarketNavigationState = {
  myListingsTab?: string;
};

export const MarketNavigation: React.FC<MarketNavigationProps> = ({
  canModerateListings = false,
  className,
}) => {
  const location = useLocation();
  const pathname = normalizePathname(location.pathname);
  const locationState = location.state as MarketNavigationState | null;
  const currentMyListingsTab = new URLSearchParams(location.search).get("tab");
  const rememberedMyListingsTab =
    pathname === ROUTES.classifiedsMy &&
    MY_LISTINGS_TABS.has(currentMyListingsTab || "")
      ? currentMyListingsTab
      : locationState?.myListingsTab;
  const navigationState = rememberedMyListingsTab
    ? { myListingsTab: rememberedMyListingsTab }
    : undefined;
  const keepCurrentQuery = (path: string): string =>
    pathname === path && location.search ? `${path}${location.search}` : path;
  const myListingsHref =
    pathname === ROUTES.classifiedsMy
      ? keepCurrentQuery(ROUTES.classifiedsMy)
      : `${ROUTES.classifiedsMy}${rememberedMyListingsTab ? `?tab=${rememberedMyListingsTab}` : ""}`;
  const tabs: TabsMap = {
    [ROUTES.classifieds]: {
      id: ROUTES.classifieds,
      name: "Все объявления",
      mobileName: "Все",
      href: keepCurrentQuery(ROUTES.classifieds),
      linkState: navigationState,
      icon: <List size={15} strokeWidth={1.8} />,
    },
    [ROUTES.classifiedsMy]: {
      id: ROUTES.classifiedsMy,
      name: "Мои объявления",
      mobileName: "Мои",
      href: myListingsHref,
      linkState: navigationState,
      icon: <FileText size={15} strokeWidth={1.8} />,
    },
    [ROUTES.classifiedsFavorites]: {
      id: ROUTES.classifiedsFavorites,
      name: "Избранное",
      mobileName: "Избранное",
      href: keepCurrentQuery(ROUTES.classifiedsFavorites),
      linkState: navigationState,
      icon: <Heart size={15} strokeWidth={1.8} />,
    },
    ...(canModerateListings
      ? {
          [ROUTES.classifiedsModeration]: {
            id: ROUTES.classifiedsModeration,
            name: "Модерация",
            mobileName: "Модерация",
            href: keepCurrentQuery(ROUTES.classifiedsModeration),
            linkState: navigationState,
            icon: <ShieldCheck size={15} strokeWidth={1.8} />,
          },
        }
      : {}),
  };

  return (
    <nav aria-label="Разделы Маркета" className={className}>
      <HHTabs
        activeTab={pathname}
        setActiveTab={() => undefined}
        tabs={tabs}
        listClassName={`!grid !w-full ${canModerateListings ? "!grid-cols-4" : "!grid-cols-3"} !items-stretch !gap-0.5 !rounded-[12px] !border-[#eadde5] !p-0.5 !shadow-none sm:!inline-flex sm:!w-fit`}
        triggerClassName="!min-h-[38px] !min-w-0 !justify-center !gap-1 !whitespace-nowrap !rounded-[9px] !px-1 !py-1.5 !text-center !text-[11px] !font-semibold !leading-none !text-[#667085] hover:!bg-[#fff7fb] hover:!text-[#8b2a5e] data-[state=active]:!border-[#efc5d8] data-[state=active]:!bg-[#fbeaf0] data-[state=active]:!text-[#8b2a5e] sm:!min-h-[38px] sm:!flex-none sm:!gap-1.5 sm:!px-3 sm:!text-[13px]"
      />
    </nav>
  );
};
